import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Ticket } from "lucide-react";
import SectionTitle from "./SectionTitle";
import { api } from "../data/api";

const statusMap = {
  on_sale: { label: "Billets", cls: "bg-gradient-to-r from-electric to-neon text-white btn-glow" },
  soon: { label: "Bientôt", cls: "border border-gold/60 text-gold" },
  sold_out: { label: "Complet", cls: "border border-white/15 text-white/40 cursor-not-allowed" },
};

const fmtDate = (d) => {
  const date = new Date(d);
  return {
    day: date.toLocaleDateString("fr-FR", { day: "2-digit" }),
    month: date.toLocaleDateString("fr-FR", { month: "short" }).toUpperCase().replace(".", ""),
  };
};

export const Concerts = () => {
  const [concerts, setConcerts] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/concerts").then((r) => {
      setConcerts(r.data);
      if (r.data.length) setActive(r.data[0]);
    }).catch(() => {});
  }, []);

  return (
    <section id="concerts" className="section bg-[#050505] py-28">
      <div className="orb orb-blue" style={{ width: 380, height: 380, top: "10%", right: "-6%" }} />
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionTitle kicker="Concerts" title="La tournée 2026" testid="concerts-title" />

        <div className="grid gap-8 lg:grid-cols-2">
          <div className="space-y-3" data-testid="concerts-list">
            {concerts.map((c, i) => {
              const dt = fmtDate(c.date);
              const st = statusMap[c.status];
              const isActive = active && active.id === c.id;
              return (
                <motion.div
                  key={c.id}
                  data-testid={`concert-item-${i}`}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  onMouseEnter={() => setActive(c)}
                  className={`glass flex items-center gap-5 rounded-2xl p-4 transition-all duration-300 ${
                    isActive ? "border-electric/60" : "hover:border-white/25"
                  }`}
                >
                  <div className="flex w-16 flex-col items-center justify-center rounded-xl bg-white/5 py-2">
                    <span className="font-display text-2xl font-black text-white">{dt.day}</span>
                    <span className="font-heading text-xs text-electric">{dt.month}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-base font-semibold text-white">{c.festival}</h3>
                    <p className="font-body text-sm text-white/60">
                      {c.venue} · {c.city}, {c.country}
                    </p>
                  </div>
                  <a
                    href={c.status === "sold_out" ? undefined : c.ticket_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`ticket-btn-${i}`}
                    className={`whitespace-nowrap rounded-full px-4 py-2 font-heading text-xs font-semibold ${st.cls}`}
                  >
                    {st.label}
                  </a>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative min-h-[400px] overflow-hidden rounded-2xl glass p-1"
          >
            {active && (
              <>
                <iframe
                  key={active.id}
                  data-testid="concert-map"
                  title="Carte des concerts"
                  className="h-full min-h-[400px] w-full rounded-xl grayscale-[0.3]"
                  style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg)" }}
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${active.lng - 0.15}%2C${active.lat - 0.1}%2C${active.lng + 0.15}%2C${active.lat + 0.1}&layer=mapnik&marker=${active.lat}%2C${active.lng}`}
                />
                <div className="absolute bottom-4 left-4 z-10 flex items-center gap-2 rounded-full glass-strong px-4 py-2">
                  <MapPin size={16} className="text-neon" />
                  <span className="font-heading text-sm text-white">{active.city}, {active.country}</span>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Concerts;
